/**
 * Scan a product — barcode or label. Spec page 02, the web half.
 *
 * This used to be a route (`/add/scan`) and is now a mode of the add sheet. The
 * readers did not change; where they sit did. A route meant the day went off
 * screen to hold a camera up to a packet, and coming back was a navigation
 * rather than a tap — so scanning a second thing meant leaving Today twice. In
 * the sheet the meal header, the ring above and the undo tray all survive it,
 * and "scan another" costs nothing.
 *
 * Two readers, and keeping them apart is the design.
 *
 * **The barcode reader is a loop, not an event.** It runs several times a
 * second against the live feed and answers from `GET /v1/foods/barcode/{code}`
 * — one indexed lookup, no photograph anywhere in it. Every frame is a free
 * attempt, so a miss costs nothing and the user simply keeps aiming until it
 * reads, the way a barcode scanner is supposed to behave.
 *
 * It did not used to work like that. A decode fired the whole photo pipeline:
 * freeze the viewfinder, capture the frame, upload it to object storage, create
 * a scan row, poll until terminal — all so the server could reach the barcode
 * rung it settles in a single query. That made every decode a commitment. The
 * feed froze on the first thing it saw, a code Open Food Facts had never heard
 * of came back as a failed *scan*, and getting another go meant "try again"
 * rather than moving the camera an inch.
 *
 * **The shutter is the label reader**, and still the photo pipeline: capture,
 * upload, OCR, poll. It is for the other case — no barcode, or a barcode
 * nobody has. A photo with neither has nothing to log and the server says so:
 * it is entered by hand.
 *
 * The last decoded code still rides along with that photo. Not to be resolved —
 * it has already missed by then — but because `process_scan` writes it onto
 * whatever OCR parses, which is what makes one person's slow label read the
 * next person's cache hit.
 *
 * **Every browser gets the loop**, including the ones with no `BarcodeDetector`
 * — Safari, Firefox, and therefore every browser on iOS, since they are all
 * WebKit underneath. `lib/barcode.ts` hands back zbar compiled to WASM where
 * the native detector is missing, so what follows does not know or care which
 * one it is driving. The camera was never the problem on iOS; only the decoder
 * was, and a decoder is just code.
 *
 * It can still come back with nothing — the WASM has to be fetched and
 * compiled, and a browser where that fails must be sent to the shutter rather
 * than told to hold a barcode in frame until it reads. That is what `canDecode`
 * is for, and why it is state rather than a constant: the answer now arrives.
 *
 * The server-side decoder stays regardless. It is what catches the photo the
 * loop never got a clean look at, and it is what writes a barcode onto a label
 * read so the next person gets the cache hit.
 *
 * **Which camera is a question only the user can settle.** `facingMode:
 * "environment"` names a side of the phone, not a lens: a phone with three rear
 * cameras answers it with whichever one the browser prefers, and that is often
 * the ultra-wide, which cannot focus on a packet held at reading distance. So
 * the picker sits on the viewfinder — every camera the device has, by name, the
 * choice remembered on the device that made it.
 *
 * The camera can also be chosen *for* you, and that is the case worth writing
 * down: a picked camera that is busy or gone falls back to one that works,
 * because a viewfinder beats an error message. Doing that silently would leave
 * somebody looking at the wrong picture next to a picker insisting on the right
 * one, so the substitution comes back on the feed and is said in words. The
 * exception is a remembered camera this machine has never had — last week's
 * laptop — which is not a failure and gets no notice.
 *
 * **A recognised barcode advances on its own.** There is no confirm tap: the
 * lookup already succeeded, and holding the packet up to the camera is the only
 * gesture the user made. Asking them to agree with an answer they can see is a
 * step that exists to make the app feel careful rather than to make it correct.
 */

import {
  ScanFailed,
  resultTrust,
  useLookupBarcode,
  useScanPhoto,
  type FoodItem,
  type ScanFailure,
} from "@calorio/core";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useCalorio } from "../app/providers";
import { getDecoder, type BarcodeDecoder } from "../lib/barcode";
import {
  CAMERA_COPY,
  CameraDenied,
  dropCamera,
  holdCamera,
  listCameras,
  switchCamera,
  type CameraFeed,
  type CameraOption,
  type CameraProblem,
} from "../lib/camera";
import { captureVisible, frameImage } from "../lib/capture";
import { ManualFood, type PartialNutrition } from "./ManualFood";

/**
 * Where a food on screen came from — and `"manual"` is one of them.
 *
 * It used to be smuggled through as a `Scan` with `id: "manual"` and a
 * `method` of `label_ocr`, which the card then had to special-case so it
 * wouldn't claim a typed-in food had been read off a label. Naming the fourth
 * source deletes both the fake scan and the special case.
 */
export type Trust = ReturnType<typeof resultTrust> | "manual";

export const TRUST_COPY: Record<Trust, string> = {
  measured: "From the product's barcode",
  transcribed: "Read off the label",
  estimated: "Estimated from the photo — check it",
  manual: "Entered by hand",
};

/**
 * How often to look for a barcode, in ms.
 *
 * Scheduled after each attempt finishes rather than on an interval, so a slow
 * `detect()` can't stack calls on top of each other — the gap is between
 * attempts, not between starts. Fast enough that holding a packet roughly still
 * reads it without the user noticing they waited; the work is a decode on a
 * frame that already exists, so this is far cheaper than the 400ms it replaces,
 * which had to be that slow only because each hit fired an upload.
 */
const LOOK_EVERY_MS = 120;

interface ScanPaneProps {
  /**
   * A food is ready. The sheet takes it from here and asks for a weight.
   *
   * All three readers land on this one callback, which is what keeps the
   * confirm state from having to know which of them it is looking at.
   */
  onFound: (food: FoodItem, trust: Trust, still?: string) => void;
  /**
   * Whether the viewfinder is frozen on a photograph.
   *
   * The sheet header owns the only way back, and what "back" means depends on
   * this: aiming at a packet, it is "back to the list"; looking at a still that
   * would not read, it is "take another photo". Reported rather than duplicated
   * so there is one answer.
   */
  onFrozen: (frozen: boolean) => void;
  /** Bumped by the header's "take another photo" to thaw the viewfinder. */
  resetToken: number;
  /**
   * The undo tray, docked above the shutter.
   *
   * Passed in rather than rendered by the sheet around this, because it belongs
   * between the hint and the button and there is no way to say that from the
   * outside. Scanning is a third way into the same meal, not a departure from
   * it: the two items already logged stay counted while you aim at the third.
   *
   * Rendered on the refusal screens as well as under the viewfinder, and that
   * is not symmetry for its own sake: this pane is also mounted inside the pot
   * builder, where the tray carries the only way back to the pot. A label that
   * would not read used to replace the whole pane with a form and take that
   * button off screen with it, leaving the sheet header — which in the pot
   * means "back to the list", and throws the pot away.
   */
  tray?: ReactNode;
}

export function ScanPane({ onFound, onFrozen, resetToken, tray }: ScanPaneProps) {
  const ctx = useCalorio();

  const videoRef = useRef<HTMLVideoElement>(null);
  /**
   * The last code the camera decoded, whatever came of looking it up.
   *
   * Held in a ref as well as in `seen` because the loop runs several times a
   * second and must not wait for a re-render to know what it has already read —
   * and because the shutter needs to send it with the photo.
   */
  const decoded = useRef<string | null>(null);
  /**
   * Codes the server has told us it does not have.
   *
   * Without this the loop asks about the same unknown packet ten times a second
   * for as long as it stays in frame. A 404 is a settled fact for the session,
   * so ask once — and keep it across `reset`, since scanning the same missing
   * product again would only produce the same answer.
   */
  const missed = useRef(new Set<string>());
  /** A lookup or an upload is in flight; don't start another read on top of it. */
  const busy = useRef(false);

  /**
   * The running camera: the stream, which camera it is, and whether it is the
   * one that was asked for. All three travel together because they change
   * together — a switch replaces every one of them at the same instant.
   */
  const [feed, setFeed] = useState<CameraFeed | null>(null);
  const stream = feed?.stream ?? null;
  /**
   * The cameras there are to choose between, once there is a feed.
   *
   * Deliberately not fetched before one: `enumerateDevices` will happily answer
   * ahead of permission, with the right number of cameras and no names for any
   * of them, and a picker offering "Camera 1 / Camera 2 / Camera 3" is a
   * question nobody can answer. See `listCameras`.
   */
  const [cameras, setCameras] = useState<CameraOption[]>([]);
  /** A switch is in flight: the old stream is stopped and the new one is not
   *  here yet, which is a black rectangle unless the screen says why. */
  const [switching, setSwitching] = useState(false);
  /**
   * Why there is no picture, at the granularity the screen answers.
   *
   * A sentence used to be stored here, and it was the same sentence for every
   * cause: a user who dismissed the permission prompt was told the device had
   * no camera. The kind is kept instead, because it decides both the wording
   * and whether "Try again" is a real offer — see `lib/camera.ts`.
   */
  const [cameraProblem, setCameraProblem] = useState<CameraProblem | null>(null);
  /** Bumped by "Try again", which is the only thing that re-asks. */
  const [retry, setRetry] = useState(0);
  /**
   * Whether there is a decoder, which decides what the screen *promises*.
   *
   * Starts false and is set when one resolves, because on WebKit that answer
   * costs a fetch and a WASM compile. False is the honest opening position: it
   * points at the shutter, which works on every browser there has ever been,
   * and the loop's promise replaces it the moment the promise can be kept.
   */
  const [canDecode, setCanDecode] = useState(false);
  const [seen, setSeen] = useState<string | null>(null);
  /** The captured frame, held so the viewfinder can freeze on it. A live feed
   *  under a spinner invites you to keep aiming at something already taken. */
  const [still, setStill] = useState<string | null>(null);
  /**
   * A barcode we read cleanly and simply do not have.
   *
   * Kept apart from `problem` because it is not a failure and must not be
   * treated as one. Nothing broke, there is nothing to retry, and the camera
   * should carry on scanning — the user aims at the next packet, or at the
   * label. `problem` stops the loop; this is a hint beside a live viewfinder.
   */
  const [unknown, setUnknown] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /**
   * Which of the two refusals the server gave, when it refused.
   *
   * The screen answers them in opposite directions — a panel we could see but
   * not read wants a better photograph, a plate with no packaging wants the
   * form — so this has to be a fact from the server rather than something
   * inferred from the wording of `problem`.
   */
  const [refusal, setRefusal] = useState<ScanFailure | null>(null);
  /** What a refused read did manage, offered for completion rather than binned. */
  const [salvage, setSalvage] = useState<PartialNutrition | null>(null);
  /**
   * The product name a half-read label yielded, when the crop had one in it.
   *
   * Usually empty — a photo framed on the nutrition panel often has no product
   * name in it — so this is one box occasionally saved, not a promise. There is
   * deliberately no brand: the reader never produces one, and an empty field is
   * honest where a guess would not be.
   */
  const [readName, setReadName] = useState("");
  /** Focus target for "Add it by hand", which points at a form already open. */
  const byHandRef = useRef<HTMLInputElement>(null);
  /**
   * Typing the food in, after a photo produced nothing.
   *
   * A scan that finds neither a barcode nor a panel is a dead end without this:
   * the server's own message says the food can be added by hand, and until this
   * existed the screen offered no way to do it.
   */
  const [byHand, setByHand] = useState(false);

  const lookup = useLookupBarcode(ctx);
  const scan = useScanPhoto(ctx);

  /**
   * Start fetching the decoder alongside the permission prompt, not after it.
   *
   * The loop below asks for one too, and would get it — but only once there is
   * a stream, which is on the far side of a dialog the user may sit on for a
   * while. On WebKit that dialog is dead time a 236KB download and a WASM
   * compile could have happened in. `getDecoder` memoises, so this is the same
   * promise the loop later awaits rather than a second decoder.
   */
  useEffect(() => {
    void getDecoder();
  }, []);

  // Borrowed, not opened. See `lib/camera.ts` for why that distinction is the
  // whole fix: scan is a *mode* of the sheet, so going back to the list and
  // returning here unmounts and remounts this pane, and a `getUserMedia` per
  // mount is a permission prompt per mount on any browser set to ask.
  //
  // The camera still goes out — `closeCamera` when the sheet closes, and a
  // linger timeout for a sheet nobody came back to. What it no longer does is
  // treat a step within scanning as the end of scanning.
  useEffect(() => {
    let cancelled = false;
    setCameraProblem(null);

    holdCamera()
      .then((running) => {
        if (!cancelled) setFeed(running);
      })
      .catch((error) => {
        if (cancelled) return;
        setFeed(null);
        setCameraProblem(error instanceof CameraDenied ? error.problem : "failed");
      });

    return () => {
      cancelled = true;
      setFeed(null);
      dropCamera();
    };
  }, [retry]);

  /**
   * What there is to choose between, asked once there is a picture.
   *
   * `devicechange` is not paranoia on a laptop: plugging in a webcam, or
   * closing the lid on the built-in one, changes the list under a sheet that
   * may have been open for a minute. The list is cheap and the alternative is a
   * picker offering a camera that is no longer there.
   */
  useEffect(() => {
    if (!stream) return;

    let cancelled = false;
    const refresh = () => {
      void listCameras().then((found) => {
        if (!cancelled) setCameras(found);
      });
    };

    refresh();
    navigator.mediaDevices?.addEventListener?.("devicechange", refresh);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.("devicechange", refresh);
    };
  }, [stream]);

  /**
   * Open the camera the user chose.
   *
   * A camera that will not open usually resolves as a *substitution* rather
   * than an error — `switchCamera` falls back to one that works and says which,
   * because a viewfinder beats a message. The catch here is the other case, the
   * one where nothing at all can be opened: that is the same dead end the first
   * acquisition has, and it gets the same placeholder with its sentence and its
   * "Try again" rather than a picker that silently does nothing.
   */
  const choose = async (id: string) => {
    const camera = cameras.find((option) => option.id === id);
    if (!camera || switching || camera.id === feed?.deviceId) return;

    setSwitching(true);
    try {
      const running = await switchCamera(camera);
      setFeed(running);
      setCameraProblem(null);
    } catch (error) {
      setFeed(null);
      setCameraProblem(error instanceof CameraDenied ? error.problem : "failed");
    } finally {
      setSwitching(false);
    }
  };

  // Attaching the stream is its own effect, and depends on `still` as well.
  //
  // Two reasons. The <video> does not exist until `stream` is set, so assigning
  // srcObject inside the getUserMedia callback ran while the ref was still null,
  // the `if` guard swallowed it, and the element mounted with no source: a
  // permission prompt followed by a black rectangle. And the element is
  // unmounted entirely while a still is showing, so coming back from "take
  // another photo" needs the stream attached again — a freshly mounted <video>
  // has no srcObject, and keying this on `stream` alone would not re-run.
  //
  // The explicit play() is for iOS Safari, which does not reliably autoplay a
  // freshly attached stream even with muted + playsInline. It rejects when the
  // element is gone or the gesture is stale, and neither is worth surfacing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream || still) return;
    video.srcObject = stream;
    void video.play().catch(() => {});
  }, [stream, still]);

  /**
   * Back to a live, scanning viewfinder.
   *
   * `missed` survives on purpose. Those codes were read perfectly and the
   * server does not have them; clearing the set would only buy the user a
   * second identical round trip for every packet they are still holding.
   */
  const reset = () => {
    decoded.current = null;
    setSeen(null);
    setStill(null);
    setUnknown(null);
    setProblem(null);
    setRefusal(null);
    setSalvage(null);
    setByHand(false);
  };

  // The header owns the way back, so the reset it triggers arrives as a token
  // rather than as a call. Skipped on the first render — mounting is already a
  // fresh scanner, and re-running reset there would clear a barcode the loop
  // had managed to read in the meantime.
  const firstToken = useRef(resetToken);
  useEffect(() => {
    if (resetToken === firstToken.current) return;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  // What "back" means, reported to the header that has to say it.
  useEffect(() => {
    onFrozen(still !== null);
  }, [still, onFrozen]);

  /**
   * A frame for the confirm state to show beside the answer, and nothing else.
   *
   * Deliberately not `frame()`: that one freezes the viewfinder, which is right
   * when a photograph is being read and wrong here — the loop found the packet
   * and the sheet is about to leave this mode anyway. Synchronous and
   * best-effort, so a browser that cannot produce it advances anyway; see
   * `frameImage`.
   */
  const thumbnail = (): string | undefined => {
    const video = videoRef.current;
    return (video && frameImage(video)) ?? undefined;
  };

  const frame = async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video) return null;

    // Only what the viewfinder was showing — see lib/capture.ts. The still and
    // the uploaded bytes are the same pixels, so the frozen frame is literally
    // the picture being read.
    const shot = await captureVisible(video);
    if (!shot) return null;
    setStill(shot.dataUrl);
    return shot.blob;
  };

  /**
   * A code came off the camera. Ask what it is — nothing more.
   *
   * No capture, no upload, no scan row. That is the whole reason the loop above
   * can afford to call this on any frame that happens to decode.
   */
  const identify = async (code: string) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const outcome = await lookup.mutateAsync(code);
      if (outcome.found) {
        onFound(outcome.food, "measured", thumbnail());
        return;
      }
      // Read fine, nobody has it. Remember so the loop stops asking, show the
      // server's own sentence — it already says to photograph the label — and
      // leave the camera running. A barcode proves this is packaging, so a
      // better photo is the right next action and the manual form is not.
      missed.current.add(code);
      setUnknown(outcome.message);
    } catch (error) {
      // Not an answer about this barcode: offline, or the session went. Say so
      // and stop the loop, rather than retrying ten times a second against
      // something that is down.
      setProblem(error instanceof Error ? error.message : "Couldn't look that up — try again.");
    } finally {
      busy.current = false;
    }
  };

  /**
   * The running camera, as the two controls need it.
   *
   * `picked` is empty rather than wrong when the browser will not say which
   * device is live: a picker showing the first camera in the list because it
   * had nothing better is a claim, and it is the one claim this control must
   * never make. `runningLabel` has the same problem and a softer answer, since
   * "using another camera" is still true and still useful.
   */
  const picked = cameras.some((camera) => camera.id === feed?.deviceId) ? feed!.deviceId : "";
  const runningLabel =
    cameras.find((camera) => camera.id === feed?.deviceId)?.label ?? "another camera";

  const submit = async () => {
    if (busy.current) return;
    busy.current = true;
    setProblem(null);
    setUnknown(null);
    setRefusal(null);
    setSalvage(null);
    setReadName("");
    const image = await frame();
    if (!image) {
      busy.current = false;
      return;
    }
    try {
      const outcome = await scan.mutateAsync({
        image,
        // Already looked up and missed, if the loop ever saw it — but the
        // server writes it onto whatever OCR parses, so the next person to scan
        // this product gets the cache hit Open Food Facts couldn't give them.
        ...(decoded.current ? { barcode: decoded.current } : {}),
      });
      if (outcome.food_item) {
        // `still` is already the photograph that was read — the same pixels the
        // server saw, which is what makes the thumbnail evidence rather than
        // decoration.
        onFound(outcome.food_item, resultTrust(outcome), still ?? undefined);
      } else {
        // A complete product scan with no food on it is the server contradicting
        // itself. Rare, but rendering nothing at all is the one response that
        // leaves the user with no idea what happened.
        setProblem("That scan came back empty — try another photo.");
      }
    } catch (error) {
      // OCR fails two ways and they need opposite responses; the server has
      // already made that call, so its message is the one worth showing.
      setProblem(error instanceof Error ? error.message : "That didn't work — try again.");

      // A refusal is not necessarily a blank. When three of four figures were
      // legible the server sends them along, and offering those beats making
      // someone retype a panel that was mostly read.
      // Which refusal this was, straight from the server. Everything the
      // screen does next hangs off this rather than off the sentence.
      const refused = error instanceof ScanFailed ? error.scan : undefined;
      setRefusal(refused?.reason ?? null);
      setReadName(refused?.read_name ?? "");

      const partial = refused?.partial;
      if (partial && Object.keys(partial).length) {
        setSalvage({
          energy: partial.energy,
          protein: partial.protein,
          carbs: partial.carbs,
          fat: partial.fat,
          // The optional four travel too. They are the rows most likely to be
          // the ones that failed — they sit lower on a panel, in smaller type,
          // under a parent — so dropping them here would ask somebody to
          // retype exactly the figures the reader did manage.
          saturates: partial.saturates,
          fiber: partial.fiber,
          sugar: partial.sugar,
          salt: partial.salt,
          sodium: partial.sodium,
        });
      }
    } finally {
      busy.current = false;
    }
  };

  /**
   * Watch the live feed for a barcode, for as long as there is a reason to.
   *
   * Self-scheduling rather than `setInterval`: the next look is booked when the
   * last one finishes, so a slow `detect()` on a big frame delays the loop
   * instead of stacking a second decode on top of the first. That was a nicety
   * with the native detector and is load-bearing with the WASM one, which on an
   * older phone can take longer than the interval it is scheduled at.
   *
   * The three things that stop it are each a different "there is no point":
   * `still` — you are looking at a photograph, not aiming, and the detector
   * would just keep re-reading the same frozen frame; `problem` — something is
   * actually broken and retrying at speed helps nobody; no `stream` or no
   * decoder — there is nothing to read. A found food used to be a fourth, and
   * is now the unmount: the sheet leaves this mode the moment one arrives.
   *
   * An unknown barcode is deliberately *not* on that list. It is the case the
   * split was for: the code read fine, we simply don't have it, and the user
   * gets to keep aiming at the next packet without touching a button.
   *
   * Getting the decoder is awaited rather than assumed, because on WebKit it is
   * a download and a compile. `live` is therefore checked again on the far side
   * of it: the screen can perfectly well have moved on — a photo taken, an
   * answer found — while the WASM was still arriving.
   */
  useEffect(() => {
    if (!stream || still || problem || switching) return;

    let live = true;
    let timer: number | undefined;

    const look = async (decoder: BarcodeDecoder) => {
      const video = videoRef.current;
      if (video?.videoWidth && !busy.current) {
        try {
          const [hit] = await decoder.detect(video);
          // A code the server has already disowned is not news. This is what
          // stops the API being asked ten times a second about the unknown
          // packet the user is still holding up to the camera — a *found* one
          // needs no such guard, because the answer unmounts this whole pane.
          if (hit && !missed.current.has(hit.rawValue)) {
            decoded.current = hit.rawValue;
            setSeen(hit.rawValue);
            await identify(hit.rawValue);
          }
        } catch {
          // A decoder that throws on one frame is not worth reporting: the
          // next frame is 120ms away, and the shutter is still there.
        }
      }
      if (live) timer = window.setTimeout(() => void look(decoder), LOOK_EVERY_MS);
    };

    void getDecoder().then((decoder) => {
      if (!live || !decoder) return;
      setCanDecode(true);
      void look(decoder);
    });

    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, still, problem, switching]);

  /**
   * A food the user typed lands exactly where a scanned one does, so finishing
   * by hand costs no extra step — pick the portion, see the calories, log it.
   *
   * It arrives labelled `"manual"`, which is what stops the card claiming these
   * numbers were read off a label. They were not; the person typed them, and
   * saying otherwise is the one thing this screen must never do.
   */
  const adopt = (created: FoodItem) => onFound(created, "manual");

  /* A panel we could see and could not finish.

     The order is the argument: the frozen frame, the reason it refused, and
     then the form already carrying whatever was legible. Making someone retype
     a panel that was three-quarters read is the thing this branch exists to
     avoid. The way back to the camera is in the sheet header, which is where
     every other way out of a mode lives. */
  if (refusal === "unreadable_panel") {
    return (
      <div className="scan scan--salvage">
        <div className="scan__refusal">
          {still && <img src={still} alt="The photo being read" className="scan__still" />}
          <div>
            <p className="error scan__refusal-line">{problem}</p>
            <p className="scan__refusal-sub">
              Check what&rsquo;s below, fill in the blanks, and save it.
            </p>
          </div>
        </div>

        {tray}

        <ManualFood
          initialName={readName}
          initial={salvage ?? {}}
          salvage
          bare
          onCancel={reset}
          onCreated={adopt}
        />
      </div>
    );
  }

  /* Nothing to read, and another photo of the same plate will not change that.
     So the emphasis inverts: the form is already open rather than waiting
     behind a button, and the two ways out carry equal weight. */
  if (refusal === "no_packaging") {
    return (
      <div className="scan scan--salvage">
        <div className="scan__refusal">
          {still && <img src={still} alt="The photo being read" className="scan__still" />}
          <div>
            <p className="error scan__refusal-line">{problem}</p>
            <p className="scan__refusal-sub">
              Calorio doesn&rsquo;t guess at a plate. If it came out of somebody&rsquo;s kitchen,
              <button className="scan__inline" onClick={() => byHandRef.current?.focus()}>
                type the numbers you know
              </button>
              .
            </p>
          </div>
        </div>

        {tray}

        <ManualFood nameRef={byHandRef} bare onCancel={reset} onCreated={adopt} />
      </div>
    );
  }

  /* Neither refusal — the connection went, or the session did. Nothing was
     read, so there is nothing to salvage and nothing to say about the photo;
     the only useful offers are another go and the form. */
  if (problem && !refusal) {
    return (
      <div className="scan scan--salvage">
        <div className="scan__refusal">
          {still && <img src={still} alt="The photo being read" className="scan__still" />}
          <div>
            <p className="error scan__refusal-line">{problem}</p>
            <p className="scan__refusal-sub">
              <button className="scan__inline" onClick={reset}>
                Try again
              </button>
              {!byHand && (
                <>
                  {" or "}
                  <button className="scan__inline" onClick={() => setByHand(true)}>
                    add it by hand
                  </button>
                </>
              )}
              .
            </p>
          </div>
        </div>

        {tray}

        {byHand && <ManualFood bare onCancel={() => setByHand(false)} onCreated={adopt} />}
      </div>
    );
  }

  return (
    <div className="scan">
      <section className="scan-cam">
        {/* The still REPLACES the feed rather than covering it.

            Both were mounted at once, on the belief that unmounting the video
            would drop the stream — which is wrong: the MediaStream lives in
            state, and the effect above re-attaches it when the still clears. The
            cost of that mistake was visible: iOS composites <video> in its own
            layer, so the paused feed painted around the edges of the image on
            top of it and the frame appeared in vertical strips. One element at a
            time is the only arrangement with nothing to get wrong. */}
        {still ? (
          <img src={still} alt="The photo being read" className="scan-cam__video" />
        ) : stream ? (
          <video ref={videoRef} autoPlay playsInline muted className="scan-cam__video" />
        ) : (
          /* No picture, and which of the reasons decides what is offered.
             "Try again" re-runs the hold, which on a browser that only refused
             once will prompt again — so it is a real offer. Where the refusal
             was remembered, or there is no camera at all, a button that cannot
             work is worse than the sentence explaining why. */
          <div className="scan-cam__placeholder muted">
            <p>{cameraProblem ? CAMERA_COPY[cameraProblem] : "Starting camera\u2026"}</p>
            {(cameraProblem === "denied" ||
              cameraProblem === "busy" ||
              cameraProblem === "failed") && (
              <button className="scan__inline" onClick={() => setRetry((n) => n + 1)}>
                Try again
              </button>
            )}
          </div>
        )}
        {(scan.isPending || lookup.isPending) && <span className="scan-working">Reading…</span>}
        {seen && <span className="scan-badge num">{seen}</span>}

        {/* Which camera, and what happened to the one you asked for.

            Over the picture rather than under it, because both are statements
            about the picture: the phone that handed back its ultra-wide, or the
            laptop whose external webcam was busy, is showing you the wrong
            thing *right there*, and the control that fixes it should be in the
            same glance as the evidence that something needs fixing.

            Only while there is a live feed. On a still the camera is not the
            subject — the photograph is — and on the placeholder there is
            nothing to choose between. */}
        {!still && stream && (cameras.length > 1 || feed?.substituted || switching) && (
          <div className="scan-cam__cameras">
            {cameras.length > 1 && (
              <select
                aria-label="Camera"
                className="scan-cam__pick"
                value={picked}
                disabled={switching}
                onChange={(event) => void choose(event.target.value)}
              >
                {/* A select whose value matches nothing shows its first option,
                    which would claim a camera we are not running. On the rare
                    browser that will not name the live device, say so instead. */}
                {!picked && (
                  <option value="" disabled>
                    Choose a camera
                  </option>
                )}
                {cameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.label}
                  </option>
                ))}
              </select>
            )}

            {/* `role="status"` on both, because a viewfinder that changes under
                you is exactly the kind of thing a screen reader user gets no
                other notice of. */}
            {switching && (
              <p className="scan-cam__note" role="status">
                Switching camera…
              </p>
            )}
            {!switching && feed?.substituted && (
              <p className="scan-cam__note" role="status">
                {feed.substituted.label} wasn&rsquo;t available — using {runningLabel}.
              </p>
            )}
          </div>
        )}
      </section>

      {/* What the camera is doing, and what the user should do about it. Without
          this the barcode loop is invisible: the feed just sits there looking
          like a screen waiting for the shutter to be pressed, which is now
          precisely the wrong thing to do everywhere. Until the decoder resolves
          this says the true thing about a screen that is not yet scanning. */}
      {!still && (
        <p className="scan__hint">
          {unknown
            ? unknown
            : canDecode
              ? "Hold the barcode in frame — it reads on its own. No barcode, or an unusual product? Photograph the nutrition label."
              : "Photograph the barcode or the nutrition label and we'll read it here."}
        </p>
      )}

      {tray}

      {/* While a photo is being read the button stops being a button and says
          which rung is running. Which rung that is depends on whether a barcode
          was decoded: with one in hand the server is doing a lookup, without one
          it is reading the panel. Naming it beats a spinner, because the two
          take visibly different lengths of time and the user is deciding whether
          to keep holding the packet up. */}
      <button
        className={`button scan__shutter${scan.isPending ? " button--working" : ""}`}
        onClick={() => void submit()}
        disabled={scan.isPending}
      >
        {scan.isPending
          ? decoded.current
            ? "Looking it up…"
            : "Reading the label…"
          : "Photograph the label"}
      </button>
    </div>
  );
}
